/**
 * Unified AGI Service - Interface to AGI capabilities
 * Supports both centralized (kernel) and decentralized (Convex + LLM) modes
 * Integrated with MoM (Mixture of Minds) routing for LLM calls
 */

import { kernelService } from "./kernelService";
import { convexService } from "./convexService";
import { authService } from "./authService";
import {
  callLLM,
  streamLLM,
  getLLMConfigs,
  buildCortexSystemPrompt,
  type LLMMessage,
  type LLMStreamChunk,
} from "./llmService";
import {
  routeLLM,
  queryWithFallback,
  recordRouting,
} from "./momRouter";
import type { AGIMessage, SystemMode } from "@/types";

class AGIService {
  private mode: SystemMode = "hybrid";

  /**
   * Set active mode
   */
  setMode(mode: SystemMode): void {
    this.mode = mode;
  }

  /**
   * Send message to AGI with streaming support
   * Returns an async generator of response chunks
   */
  async *streamMessage(message: string, history: AGIMessage[] = []): AsyncGenerator<string> {
    // Check authentication
    const session = authService.getSession();
    if (!session) {
      throw new Error("Authentication required. Please login to access AGI.");
    }

    switch (this.mode) {
      case "centralized":
        // Kernel doesn't support streaming, fall back to non-streaming
        const kernelResponse = await this.sendMessageToKernel(message);
        yield kernelResponse;
        return;

      case "decentralized":
        yield* this.streamMessageToLLM(message, history);
        return;

      case "hybrid":
        // Try kernel first (non-streaming), then fallback to LLM streaming
        const kernelAvailable = await kernelService.isAvailable();
        if (kernelAvailable) {
          try {
            const response = await this.sendMessageToKernel(message);
            yield response;
            return;
          } catch (error) {
            console.warn("Kernel failed, falling back to LLM streaming:", error);
          }
        }
        yield* this.streamMessageToLLM(message, history);
        return;

      default:
        throw new Error(`Unknown mode: ${this.mode}`);
    }
  }

  /**
   * Send message to LLM with MoM routing (streaming)
   */
  private async *streamMessageToLLM(
    message: string,
    history: AGIMessage[]
  ): AsyncGenerator<string> {
    // Get settings from store
    const { useDashboardStore } = await import("@/store");
    const { settings, authLevel, addLogEntry } = useDashboardStore.getState();

    // Build LLM configs from settings
    const configs = getLLMConfigs(settings);

    if (configs.length === 0) {
      throw new Error(
        "No LLM configured. Please add API keys in Settings → API Configuration."
      );
    }

    // Determine routing based on user message
    const route = routeLLM(message);

    addLogEntry({
      category: "agi",
      action: "LLM Stream Initiated",
      details: `Streaming to ${route.provider} (${route.confidence.toFixed(2)} confidence)`,
      severity: "info",
      source: "agi-service",
    });

    // Build messages list with system prompt
    const systemPrompt = buildCortexSystemPrompt({
      authLevel: authLevel as any,
      isAuthenticated: true,
    });

    const messages: LLMMessage[] = [
      { role: "system", content: systemPrompt },
      ...history.map((h) => ({
        role: h.role as "user" | "assistant",
        content: h.content,
      })),
      { role: "user", content: message },
    ];

    const startTime = Date.now();

    // Try primary provider first
    const config = configs.find((c) => c.provider === route.provider) || configs[0];

    try {
      for await (const chunk of streamLLM(messages, config)) {
        yield chunk.delta;
      }

      const latency = Date.now() - startTime;

      // Record routing for analytics
      recordRouting({
        route,
        attemptedProviders: [config.provider],
        successProvider: config.provider,
        latency,
      });

      addLogEntry({
        category: "agi",
        action: "LLM Stream Completed",
        details: `Completed in ${latency}ms using ${config.provider}`,
        severity: "info",
        source: "agi-service",
      });
    } catch (error) {
      // Try fallback providers if enabled
      if (settings.enableFallbackModels && configs.length > 1) {
        addLogEntry({
          category: "agi",
          action: "LLM Fallback",
          details: `${config.provider} failed, trying fallback`,
          severity: "warning",
          source: "agi-service",
        });

        for (const fallbackConfig of configs) {
          if (fallbackConfig.provider === config.provider) continue;

          try {
            for await (const chunk of streamLLM(messages, fallbackConfig)) {
              yield chunk.delta;
            }

            addLogEntry({
              category: "agi",
              action: "LLM Fallback Success",
              details: `Switched to ${fallbackConfig.provider}`,
              severity: "info",
              source: "agi-service",
            });
            return;
          } catch (fallbackError) {
            console.warn(`Fallback to ${fallbackConfig.provider} also failed:`, fallbackError);
          }
        }
      }

      throw new Error(`LLM streaming failed: ${error}`);
    }
  }

  /**
   * Send message to AGI
   * Routes:
   * - Centralized: Local kernel for full AGI capabilities
   * - Decentralized: LLM via MoM routing (Convex for auth)
   * - Hybrid: Kernel first, fallback to LLM
   */
  async sendMessage(message: string, history: AGIMessage[] = []): Promise<string> {
    // Check authentication
    const session = authService.getSession();
    if (!session) {
      throw new Error("Authentication required. Please login to access AGI.");
    }

    switch (this.mode) {
      case "centralized":
        return this.sendMessageToKernel(message);

      case "decentralized":
        return this.sendMessageToLLM(message, history);

      case "hybrid":
        return this.sendMessageHybrid(message, history);

      default:
        throw new Error(`Unknown mode: ${this.mode}`);
    }
  }

  /**
   * Generate code/files using kernel
   */
  async generateAndWrite(task: string): Promise<{ success: boolean; written: number; message: string }> {
    // This requires centralized kernel
    try {
      const written = await kernelService.generateAndWrite(task);
      return {
        success: true,
        written,
        message: `Successfully wrote ${written} file(s)`,
      };
    } catch (error) {
      return {
        success: false,
        written: 0,
        message: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Execute a system action using kernel
   */
  async executeAction(action: string, params: Record<string, any> = {}): Promise<any> {
    try {
      return await kernelService.executeAction(action, params);
    } catch (error) {
      throw new Error(`Action failed: ${error}`);
    }
  }

  /**
   * Get AGI capabilities based on current mode
   */
  getCapabilities(): {
    generate: boolean;
    writeFiles: boolean;
    executeActions: boolean;
    history: boolean;
    contextManagement: boolean;
    llmRouting: boolean;
  } {
    // Kernel (centralized) has full capabilities
    if (this.mode === "centralized" || this.mode === "hybrid") {
      return {
        generate: true,
        writeFiles: true,
        executeActions: true,
        history: true,
        contextManagement: true,
        llmRouting: false,
      };
    }

    // Convex + LLM (decentralized) has chat via MoM routing
    return {
      generate: true,
      writeFiles: false,
      executeActions: false,
      history: true,
      contextManagement: true,
      llmRouting: true, // MoM routing enabled
    };
  }

  // =========== Private Methods ===========

  /**
   * Send message to local kernel
   */
  private async sendMessageToKernel(message: string): Promise<string> {
    try {
      // Use generate endpoint
      const response = await kernelService.generate(message);
      return response;
    } catch (error) {
      throw new Error(`Kernel communication failed: ${error}`);
    }
  }

  /**
   * Send message to LLM with MoM routing
   * This is decentralized path using LLM services
   */
  private async sendMessageToLLM(message: string, history: AGIMessage[]): Promise<string> {
    // Get settings from store
    const { useDashboardStore } = await import("@/store");
    const { settings, authLevel, addLogEntry } = useDashboardStore.getState();

    // Build LLM configs from settings
    const configs = getLLMConfigs(settings);

    if (configs.length === 0) {
      throw new Error(
        "No LLM configured. Please add API keys in Settings → API Configuration."
      );
    }

    // Determine routing based on user message
    const route = routeLLM(message);

    addLogEntry({
      category: "agi",
      action: "LLM Route Decision",
      details: `Routing to ${route.provider} (${route.confidence.toFixed(2)} confidence): ${route.reason}`,
      severity: "info",
      source: "agi-service",
    });

    // Build messages list with system prompt
    const systemPrompt = buildCortexSystemPrompt({
      authLevel: authLevel as any,
      isAuthenticated: true,
    });

    const messages: LLMMessage[] = [
      { role: "system", content: systemPrompt },
      ...history.map((h) => ({
        role: h.role as "user" | "assistant",
        content: h.content,
      })),
      { role: "user", content: message },
    ];

    const startTime = Date.now();

    // Query with fallback support
    try {
      const result = await queryWithFallback(
        messages,
        configs,
        callLLM,
        settings.enableFallbackModels
      );

      const latency = Date.now() - startTime;

      // Record routing for analytics
      recordRouting({
        route,
        attemptedProviders: result.attempts,
        successProvider: result.provider,
        latency,
      });

      addLogEntry({
        category: "agi",
        action: "LLM Response Received",
        details: `Using ${result.provider} after ${result.attempts.join(" → ")}`,
        severity: "info",
        source: "agi-service",
      });

      return result.response.content;
    } catch (error) {
      throw new Error(`LLM query failed: ${error}`);
    }
  }

  /**
   * Send message using hybrid approach
   * Tries kernel first, falls back to LLM + Convex
   */
  private async sendMessageHybrid(message: string, history: AGIMessage[]): Promise<string> {
    const kernelAvailable = await kernelService.isAvailable();

    if (kernelAvailable) {
      try {
        return await this.sendMessageToKernel(message);
      } catch (error) {
        console.warn("Kernel failed, falling back to LLM:", error);
      }
    }

    // Fall back to LLM (decentralized path)
    return this.sendMessageToLLM(message, history);
  }
}

// Export singleton instance
export const agiService = new AGIService();
